import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchMatchProposals, fetchMyMatchProposals, createMatchProposal,
    acceptMatchProposal, cancelMatchProposal, checkCourtAvailability,
    fetchLeagueView, getUserData, redirectToLoginIfInvalidToken
} from '../../api/api';
import Spinner from '../Spinner/Spinner';
import CustomModal from '../../UI/Modal/CustomModal';
import styles from './MatchPlanning.module.css';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const formatProposalDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const day = format(date, 'dd', { locale: pl });
    const month = format(date, 'LLLL', { locale: pl });
    const dayName = format(date, 'EEEE', { locale: pl });
    return `${day} ${month} (${dayName})`;
};

const extractErrorMessage = (data) => {
    if (!data) return 'Nieznany błąd';
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    return JSON.stringify(data);
};

const MatchPlanning = () => {
    const { tournamentId } = useParams();
    const navigate = useNavigate();

    const [leagueData, setLeagueData] = useState(null);
    const [userData, setUserData] = useState({});
    const [proposals, setProposals] = useState([]);
    const [myProposals, setMyProposals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [proposedDate, setProposedDate] = useState('');
    const [proposedTime, setProposedTime] = useState('');
    const [availability, setAvailability] = useState(null);
    const [isChecking, setIsChecking] = useState(false);

    const [modalMessage, setModalMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPositive, setIsPositive] = useState(true);
    const [confirmCallback, setConfirmCallback] = useState(null);

    useEffect(() => {
        redirectToLoginIfInvalidToken(navigate, `/leagues/${tournamentId}/match-planning`);
    }, [navigate, tournamentId]);

    useEffect(() => {
        const fetchUser = async () => {
            const data = await getUserData();
            setUserData(data);
        };
        fetchUser();
    }, []);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const leagueResult = await fetchLeagueView(tournamentId, null);
        if (leagueResult.success) {
            setLeagueData(leagueResult.data);
            const round = leagueResult.data.currentRoundNumber;

            const [proposalsResult, myResult] = await Promise.all([
                fetchMatchProposals(tournamentId, round),
                fetchMyMatchProposals(tournamentId)
            ]);

            if (proposalsResult.success) setProposals(proposalsResult.data);
            if (myResult.success) setMyProposals(myResult.data);
        }
        setIsLoading(false);
    }, [tournamentId]);

    useEffect(() => { loadData(); }, [loadData]);

    const showModal = (message, positive = true) => {
        setModalMessage(message);
        setIsPositive(positive);
        setConfirmCallback(null);
        setIsModalOpen(true);
    };

    const showConfirm = (message, onConfirm, positive = true) => {
        setModalMessage(message);
        setIsPositive(positive);
        setConfirmCallback(() => onConfirm);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setConfirmCallback(null);
    };

    const handleModalConfirm = () => {
        const cb = confirmCallback;
        closeModal();
        if (cb) cb();
    };

    const handleCheckAvailability = async () => {
        if (!proposedDate || !proposedTime) {
            showModal('Wybierz datę i godzinę', false);
            return;
        }
        setIsChecking(true);
        const result = await checkCourtAvailability(tournamentId, proposedDate, proposedTime);
        if (result.success) {
            setAvailability(result.data);
        } else {
            showModal('Błąd sprawdzania dostępności: ' + extractErrorMessage(result.data), false);
        }
        setIsChecking(false);
    };

    const handleCreate = async () => {
        if (!proposedDate || !proposedTime) {
            showModal('Wybierz datę i godzinę', false);
            return;
        }
        const result = await createMatchProposal(tournamentId, proposedDate, proposedTime);
        if (result.success) {
            showModal('Propozycja terminu dodana');
            setProposedDate('');
            setProposedTime('');
            setAvailability(null);
            loadData();
        } else {
            showModal(extractErrorMessage(result.data), false);
        }
    };

    const doAccept = async (proposalId, force) => {
        const result = await acceptMatchProposal(tournamentId, proposalId, force);
        if (result.success) {
            showModal('Mecz zaakceptowany! Rezerwacja kortu utworzona. Sprawdź email.');
        } else {
            const msg = extractErrorMessage(result.data);
            // Fallback: if server still reports ALREADY_AGREED (stale data), prompt to refresh
            if (msg === 'ALREADY_AGREED') {
                showModal('Jesteście już umówieni na ten mecz. Odśwież stronę i spróbuj ponownie.', false);
            } else {
                showModal(msg, false);
            }
        }
        loadData();
    };

    const handleAccept = (proposalId, opponentParticipantId) => {
        const alreadyAgreed = hasAcceptedProposalWith(opponentParticipantId);
        if (alreadyAgreed) {
            showConfirm(
                'Umówiłeś się już z tym graczem. Czy chcesz się ponownie umówić? Poprzedni termin zostanie anulowany wraz z rezerwacją kortu.',
                () => doAccept(proposalId, true),
                false
            );
        } else {
            showConfirm(
                'Czy na pewno chcesz przyjąć tę propozycję? Zostanie utworzona rezerwacja kortu.',
                () => doAccept(proposalId, false)
            );
        }
    };

    const doCancel = async (proposalId) => {
        const result = await cancelMatchProposal(tournamentId, proposalId);
        if (result.success) {
            showModal('Propozycja anulowana');
            loadData();
        } else {
            showModal(extractErrorMessage(result.data), false);
        }
    };

    const handleCancel = (proposalId, status) => {
        const message = status === 'ACCEPTED'
            ? 'Czy na pewno chcesz anulować umówiony mecz? Rezerwacja kortu zostanie usunięta, a rywal straci wcześniej potwierdzony termin.'
            : 'Czy na pewno chcesz anulować tę propozycję?';
        showConfirm(message, () => doCancel(proposalId), false);
    };

    if (isLoading || !leagueData) return <Spinner />;

    const currentRound = leagueData.currentRoundNumber;
    const userRoles = new Set(userData.authorities || []);
    const isSupervisorOrAdmin = userRoles.has("ROLE_SUPERVISOR") || userRoles.has("ROLE_SUPER") || userRoles.has("ROLE_ADMIN");
    const isFrozen = leagueData.frozenInCurrentRound === true;
    const userGroupLabel = leagueData.userGroupLabel;

    // Filter groups: regular player sees only their group, admin/supervisor sees all
    const visibleGroups = isSupervisorOrAdmin
        ? leagueData.groupStandings
        : leagueData.groupStandings.filter(g => g.groupLabel === userGroupLabel);

    // Get current user's participantId from league data
    const myParticipantId = leagueData.userParticipantId || null;

    // Build set of played match pairs (participantId pairs where winner is set)
    const allMatches = leagueData.groupStandings.flatMap(g => g.matches);
    const hasPlayedMatch = (pid1, pid2) => {
        return allMatches.some(m =>
            m.participantWinnerId != null &&
            ((m.participant1Id === pid1 && m.participant2Id === pid2) ||
             (m.participant1Id === pid2 && m.participant2Id === pid1))
        );
    };

    // Does current user already have an ACCEPTED proposal with the given opponent in this round?
    // Scans the full proposals list (both proposer and accepter sides).
    const hasAcceptedProposalWith = (opponentParticipantId) => {
        if (!myParticipantId || !opponentParticipantId) return false;
        return proposals.some(p =>
            p.status === 'ACCEPTED' &&
            ((p.proposerParticipantId === myParticipantId && p.accepterParticipantId === opponentParticipantId) ||
             (p.proposerParticipantId === opponentParticipantId && p.accepterParticipantId === myParticipantId))
        );
    };

    // Check if current player has any unplayed matches left
    const hasUnplayedMatches = myParticipantId && allMatches.some(m =>
        m.participantWinnerId == null &&
        (m.participant1Id === myParticipantId || m.participant2Id === myParticipantId)
    );

    // Group proposals by group label, sorted by date+time ascending
    const activeProposals = proposals
        .filter(p => p.status === 'ACTIVE')
        .sort((a, b) => {
            const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
            if (dateCompare !== 0) return dateCompare;
            return a.proposedStartTime.localeCompare(b.proposedStartTime);
        });
    const groupedProposals = {};
    for (const group of visibleGroups) {
        groupedProposals[group.groupLabel] = activeProposals.filter(p => p.groupLabel === group.groupLabel);
    }

    // Current week boundaries (Monday–Sunday)
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = format(new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset), 'yyyy-MM-dd');
    const weekEnd = format(new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset + 6), 'yyyy-MM-dd');

    // Count my proposals for status banner — current week only
    const myWeekProposals = myProposals.filter(p =>
        (p.status === 'ACTIVE' || p.status === 'ACCEPTED') &&
        p.proposedDate >= weekStart && p.proposedDate <= weekEnd
    );
    const totalProposed = myWeekProposals.length;
    const afterFiveCount = myWeekProposals.filter(p => {
        const hour = parseInt(p.proposedStartTime.split(':')[0]);
        return hour >= 17;
    }).length;

    const timeOptions = [];
    for (let h = 7; h <= 20; h++) {
        timeOptions.push(`${String(h).padStart(2, '0')}:00`);
        if (h < 20) timeOptions.push(`${String(h).padStart(2, '0')}:30`);
    }

    const getStatusClass = (status) => {
        switch (status) {
            case 'ACTIVE': return styles.statusActive;
            case 'ACCEPTED': return styles.statusAccepted;
            case 'EXPIRED': return styles.statusExpired;
            case 'CANCELED': return styles.statusCanceled;
            default: return '';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'ACTIVE': return 'Aktywna';
            case 'ACCEPTED': return 'Zaakceptowana';
            case 'EXPIRED': return 'Wygasła';
            case 'CANCELED': return 'Anulowana';
            default: return status;
        }
    };

    return (
        <div className={styles.container}>
            <button className={styles.backButton} onClick={() => navigate('/tournaments', { state: { activeTab: 'current', tournamentId: parseInt(tournamentId) } })}>
                ← Powrót
            </button>

            <h2 className={styles.title}>Planowanie meczów — Runda {currentRound}</h2>

            {isFrozen ? (
                <div className={styles.frozenBanner}>
                    ❄️ Twój udział w tej rundzie jest zamrożony — nie możesz proponować ani akceptować terminów.
                </div>
            ) : !hasUnplayedMatches ? (
                <div className={styles.frozenBanner}>
                    Rozegrałeś już wszystkie mecze w tej rundzie lub Twój udział jest zamrożony.
                </div>
            ) : (
                <>
                    <div className={styles.statusBanner}>
                        Bieżący tydzień: <span className={totalProposed >= 2 ? styles.statusOk : styles.statusWarning}>
                            {totalProposed}/2
                        </span>
                        {' | '}
                        Po 17:00: <span className={afterFiveCount >= 1 ? styles.statusOk : styles.statusWarning}>
                            {afterFiveCount}/1
                        </span>
                    </div>

                    {/* Propose a new time */}
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Zaproponuj termin</h3>
                        <div className={styles.formStack}>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Data</label>
                                <input
                                    className={styles.formInput}
                                    type="date"
                                    value={proposedDate}
                                    onChange={(e) => { setProposedDate(e.target.value); setAvailability(null); }}
                                    min={format(new Date(), 'yyyy-MM-dd')}
                                    max={leagueData.roundEndDate ? format(new Date(leagueData.roundEndDate), 'yyyy-MM-dd') : undefined}
                                />
                            </div>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Godzina</label>
                                <select className={styles.formInput} value={proposedTime} onChange={(e) => { setProposedTime(e.target.value); setAvailability(null); }}>
                                    <option value="">— wybierz —</option>
                                    {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className={styles.formButtons}>
                                <button className={styles.checkButton} onClick={handleCheckAvailability} disabled={isChecking}>
                                    {isChecking ? 'Sprawdzam...' : 'Sprawdź dostępność'}
                                </button>
                                <button
                                    className={styles.addButton}
                                    onClick={handleCreate}
                                    disabled={!availability || !availability.available}
                                >
                                    Dodaj propozycję
                                </button>
                            </div>
                        </div>
                        {availability && (
                            <div className={`${styles.availabilityInfo} ${availability.available ? styles.availabilityOk : styles.availabilityFail}`}>
                                {availability.available
                                    ? 'Termin dostępny'
                                    : (availability.message || 'Brak dostępnych kortów')
                                }
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Active proposals per group */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Propozycje terminów{!isSupervisorOrAdmin && userGroupLabel ? ` — Grupa ${userGroupLabel}` : ''}</h3>
                {visibleGroups.map(group => (
                    <div key={group.groupLabel} className={styles.groupBlock}>
                        {isSupervisorOrAdmin && <div className={styles.groupLabel}>Grupa {group.groupLabel}</div>}
                        {groupedProposals[group.groupLabel]?.length > 0 ? (
                            <table className={styles.proposalsTable}>
                                <thead>
                                    <tr>
                                        <th>Gracz</th>
                                        <th>Data</th>
                                        <th>Godzina</th>
                                        {!isFrozen && <th>Akcja</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedProposals[group.groupLabel].map(p => {
                                        const isOwnProposal = myParticipantId && p.proposerParticipantId === myParticipantId;
                                        const alreadyPlayed = myParticipantId && hasPlayedMatch(myParticipantId, p.proposerParticipantId);
                                        const canAccept = !isOwnProposal && !alreadyPlayed;
                                        return (
                                            <tr key={p.id}>
                                                <td>{p.proposerName}</td>
                                                <td>{formatProposalDate(p.proposedDate)}</td>
                                                <td>{p.proposedStartTime.substring(0, 5)}–{p.proposedEndTime.substring(0, 5)}</td>
                                                {!isFrozen && (
                                                    <td>
                                                        {canAccept && (
                                                            <button className={styles.acceptButton} onClick={() => handleAccept(p.id, p.proposerParticipantId)}>
                                                                Przyjmij
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.noProposals}>Brak aktywnych propozycji</div>
                        )}
                    </div>
                ))}
            </div>

            {/* My proposals */}
            {!isFrozen && (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Moje propozycje</h3>
                    {myProposals.length > 0 ? (
                        <table className={styles.proposalsTable}>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Godzina</th>
                                    <th>Status</th>
                                    <th>Akcja</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...myProposals].sort((a, b) => {
                                    const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
                                    if (dateCompare !== 0) return dateCompare;
                                    return a.proposedStartTime.localeCompare(b.proposedStartTime);
                                }).map(p => (
                                    <tr key={p.id}>
                                        <td>{formatProposalDate(p.proposedDate)}</td>
                                        <td>{p.proposedStartTime.substring(0, 5)}–{p.proposedEndTime.substring(0, 5)}</td>
                                        <td>
                                            <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                {getStatusLabel(p.status)}
                                            </span>
                                        </td>
                                        <td>
                                            {(p.status === 'ACTIVE' || p.status === 'ACCEPTED') && (
                                                <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                    Anuluj
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className={styles.noProposals}>Nie masz jeszcze żadnych propozycji</div>
                    )}
                </div>
            )}

            <CustomModal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                message={modalMessage}
                isPositiveMessage={isPositive}
                confirmLabel={confirmCallback ? 'Tak' : undefined}
                onConfirm={confirmCallback ? handleModalConfirm : undefined}
                cancelLabel={confirmCallback ? 'Anuluj' : 'Zamknij'}
                cancelButtonColor={confirmCallback ? undefined : 'green'}
                showConfirmButton={!!confirmCallback}
            />
        </div>
    );
};

export default MatchPlanning;
